import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  Stethoscope, User, Activity, Heart, Droplets, FlaskConical, Dumbbell, Scale,
  Ruler, Beaker, ShieldAlert, Download, Save, Loader2, CheckCircle, Sparkles,
  ArrowRight, MessageCircle
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SEOSchema from '../components/SEOSchema';

import { NumberField, SelectField, GenderField } from '../components/calculators/FormFields';import HealthGauge from '../components/health/HealthGauge';
import MetricCard from '../components/health/MetricCard';
import ScoreBarCard from '../components/health/ScoreBarCard';

import {
  computeAll, computeScores, computeCompleteness, buildSummary, buildRecommendations, scoreColor
} from '../lib/healthAnalysis';
import { WHATSAPP_NUMBER, whatsappMessages } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_OPTIONS = [
  { value: '', label: 'Select activity level' },
  { value: 'sedentary', label: 'Sedentary (little or no exercise)' },
  { value: 'light', label: 'Lightly Active (1–3 days/week)' },
  { value: 'moderate', label: 'Moderately Active (3–5 days/week)' },
  { value: 'active', label: 'Very Active (6–7 days/week)' },
  { value: 'athlete', label: 'Athlete (twice daily training)' }
];

const Section = ({ title, icon: Icon, accent, children, testId }) => (
  <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-5 sm:p-6" data-testid={testId}>
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-9 h-9 rounded-xl ${accent} flex items-center justify-center`}>
        {Icon && <Icon className="w-4 h-4 text-white" aria-hidden="true" />}
      </div>
      <h3 className="text-base sm:text-lg font-bold text-white">{title}</h3>
    </div>
    {children}
  </section>
);

const initialState = {
  full_name: '', email: '', phone: '',
  age: '', gender: '', height_cm: '', weight_kg: '',
  waist_cm: '', hip_cm: '', neck_cm: '', activity_level: '',
  sbp: '', dbp: '',
  hba1c: '', fbs: '', ppbs: '', fasting_insulin: '',
  total_cholesterol: '', ldl: '', hdl: '', triglycerides: '',
  resting_hr: '', body_fat_pct: ''
};

const HealthAnalysisPage = () => {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [downloadPending, setDownloadPending] = useState(false);
  const [analysisUnlocked, setAnalysisUnlocked] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [haConsent, setHaConsent] = useState(false);
  const reportRef = useRef(null);

  const update = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const emailIsValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email || '').trim()),
    [form.email]
  );

  const handleUnlock = async () => {
    setUnlockError('');
    if (!form.full_name.trim()) {
      setUnlockError('Please enter your full name.');
      return;
    }
    if (!emailIsValid) {
      setUnlockError('Please enter a valid email address.');
      return;
    }
    if (!haConsent) {
      setUnlockError('Please accept the medical disclaimer to continue.');
      return;
    }
    setUnlocking(true);
    try {
      // Save the lead immediately so the email is captured even if the
      // user never clicks "Save My Report" later.
      await axios.post(`${API}/health-analyses`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || null,
        unlocked_at: new Date().toISOString(),
        source: 'health_analysis_unlock',
        consent: haConsent,
      });
      setAnalysisUnlocked(true);
    } catch (e) {
      console.error(e);
      // Still unlock; backend save is non-blocking for UX
      setAnalysisUnlocked(true);
    } finally {
      setUnlocking(false);
    }
  };

  // Build numeric-form input for engine
  const input = useMemo(() => {
    const x = { ...form };
    const stringKeys = new Set(['full_name', 'email', 'phone', 'gender', 'activity_level']);
    for (const k of Object.keys(x)) {
      if (stringKeys.has(k)) continue;
      // Convert empty strings to null and numeric strings to numbers
      if (x[k] === '' || x[k] === null || x[k] === undefined) {
        x[k] = null;
      } else if (typeof x[k] === 'string') {
        const n = parseFloat(x[k]);
        x[k] = isNaN(n) ? null : n;
      }
    }
    return x;
  }, [form]);

  const computed = useMemo(() => computeAll(input), [input]);
  const scores = useMemo(() => computeScores(computed, input), [computed, input]);
  const completeness = useMemo(() => computeCompleteness(input), [input]);
  const summary = useMemo(() => buildSummary(computed, scores, input), [computed, scores, input]);
  const recommendations = useMemo(() => buildRecommendations(computed, scores, input), [computed, scores, input]);

  const radarData = useMemo(() => ([
    { axis: 'Metabolic', score: scores.metabolic ?? 0 },
    { axis: 'Cardio', score: scores.cardiovascular ?? 0 },
    { axis: 'Diabetes', score: scores.diabetes ?? 0 },
    { axis: 'Obesity', score: scores.obesity ?? 0 },
    { axis: 'Fitness', score: scores.fitness ?? 0 }
  ]), [scores]);

  const insufficientHints = useMemo(() => {
    const hints = [];
    if (input.fasting_insulin == null) hints.push('Add fasting insulin to unlock insulin resistance (HOMA-IR) analysis.');
    if (input.ldl == null || input.hdl == null || input.triglycerides == null) hints.push('Add lipid profile values to unlock cardiovascular risk scoring.');
    if (input.hba1c == null) hints.push('Add HbA1c to unlock glycemic and diabetes risk analysis.');
    if (input.sbp == null || input.dbp == null) hints.push('Add blood pressure to unlock cardiovascular analysis.');
    if (input.waist_cm == null) hints.push('Add waist circumference to unlock central obesity analysis.');
    if (input.body_fat_pct == null) hints.push('Add body fat % to unlock body composition analysis.');
    return hints;
  }, [input]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = { ...input, computed, scores, summary, recommendations, completeness: completeness.percent, consent: haConsent };
      await axios.post(`${API}/health-analyses`, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (e) {
      console.error(e);
      alert('Could not save. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!analysisUnlocked) {
      alert('Please enter your name and email to unlock your report first.');
      return;
    }
    if (!reportRef.current) return;
    setDownloadPending(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#020617',
        scale: 2,
        useCORS: true,
        logging: false
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const ts = new Date().toISOString().slice(0, 10);
      const safeName = (form.full_name || 'Tcube').replace(/[^a-z0-9]+/gi, '_').slice(0, 32);
      pdf.save(`Tcube_Full_Body_Health_Analysis_${safeName}_${ts}.pdf`);
    } catch (e) {
      console.error(e);
      alert('Could not generate PDF. Please try again.');
    } finally {
      setDownloadPending(false);
    }
  };

  const overallMeta = scoreColor(scores.overall);
  const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessages.general)}`;

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Tcube Full Body Health Analysis: Premium Preventive Health Dashboard</title>
        <meta name="description" content="Tcube Full Body Health Analysis is a doctor-led preventive health and metabolic intelligence dashboard combining diabetes, cardiovascular, obesity, fitness, and lifestyle medicine insights." />
        <link rel="canonical" href="https://tcubefitness.com/health-analysis" />
      </Helmet>

      <SEOSchema
        type="calculator"
        name="Tcube Full Body Health Analysis"
        description="A doctor-led preventive health and metabolic intelligence dashboard combining diabetes, cardiovascular, obesity, fitness and lifestyle medicine insights."
        url="/health-analysis"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Health Analysis', path: '/health-analysis' },
        ]}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 sm:pt-36 pb-24 lg:pb-16">
        <div className="container mx-auto px-4 sm:px-6">

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-10 sm:mb-14">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-5">
              <Stethoscope className="w-4 h-4" aria-hidden="true" />
              Doctor-Led · Preventive Health
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" data-testid="health-analysis-heading">
              Tcube{' '}
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                Full Body Health Analysis
              </span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Combine diabetes, cardiovascular, metabolic, obesity, fitness and lifestyle medicine insights into one premium dashboard. Add what you know, and analysis runs progressively as data is entered.
            </p>
          </section>

          {/* Completeness banner */}
          <section className="max-w-7xl mx-auto mb-8">
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-slate-300 text-sm font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-blue-400" aria-hidden="true" />
                      Health Data Completeness
                    </p>
                    <p className="text-blue-400 text-sm font-bold tabular-nums" data-testid="completeness-percent">{completeness.percent}%</p>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-700" style={{ width: `${completeness.percent}%` }}></div>
                  </div>
                  <p className="text-slate-500 text-xs mt-2">{completeness.filled} of {completeness.total} markers added. More data unlocks deeper analysis.</p>
                </div>
              </div>
            </div>
          </section>

          <div className="grid lg:grid-cols-12 gap-6 max-w-7xl mx-auto">

            {/* LEFT: Input form */}
            <div className="lg:col-span-5 xl:col-span-4 space-y-5">
              <Section title="Personal Details" icon={User} accent="bg-gradient-to-br from-blue-600 to-cyan-600" testId="section-personal">
                <div className="space-y-3">
                  <NumberField id="age" label="Age" value={form.age} onChange={update('age')} placeholder="e.g. 32" suffix="yrs" min={1} max={120} testId="ha-age" />
                  <GenderField value={form.gender} onChange={update('gender')} testIdPrefix="ha-gender" />
                  <div className="grid grid-cols-2 gap-3">
                    <NumberField id="height" label="Height" value={form.height_cm} onChange={update('height_cm')} placeholder="e.g. 170" suffix="cm" testId="ha-height" />
                    <NumberField id="weight" label="Weight" value={form.weight_kg} onChange={update('weight_kg')} placeholder="e.g. 70" suffix="kg" testId="ha-weight" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <NumberField id="waist" label="Waist" value={form.waist_cm} onChange={update('waist_cm')} placeholder="cm" suffix="cm" testId="ha-waist" />
                    <NumberField id="hip" label="Hip" value={form.hip_cm} onChange={update('hip_cm')} placeholder="cm" suffix="cm" testId="ha-hip" />
                    <NumberField id="neck" label="Neck" value={form.neck_cm} onChange={update('neck_cm')} placeholder="cm" suffix="cm" testId="ha-neck" />
                  </div>
                  <SelectField id="activity" label="Activity Level" value={form.activity_level} onChange={update('activity_level')} options={ACTIVITY_OPTIONS} testId="ha-activity" />
                </div>
              </Section>

              <Section title="Blood Pressure" icon={Heart} accent="bg-gradient-to-br from-rose-600 to-pink-600" testId="section-bp">
                <div className="grid grid-cols-2 gap-3">
                  <NumberField id="sbp" label="Systolic (SBP)" value={form.sbp} onChange={update('sbp')} placeholder="e.g. 120" suffix="mmHg" testId="ha-sbp" />
                  <NumberField id="dbp" label="Diastolic (DBP)" value={form.dbp} onChange={update('dbp')} placeholder="e.g. 80" suffix="mmHg" testId="ha-dbp" />
                </div>
              </Section>

              <Section title="Diabetes Markers" icon={Droplets} accent="bg-gradient-to-br from-amber-600 to-orange-600" testId="section-diabetes">
                <div className="grid grid-cols-2 gap-3">
                  <NumberField id="hba1c" label="HbA1c" value={form.hba1c} onChange={update('hba1c')} placeholder="e.g. 5.6" suffix="%" testId="ha-hba1c" />
                  <NumberField id="fbs" label="Fasting Glucose" value={form.fbs} onChange={update('fbs')} placeholder="e.g. 92" suffix="mg/dL" testId="ha-fbs" />
                  <NumberField id="ppbs" label="Postprandial" value={form.ppbs} onChange={update('ppbs')} placeholder="e.g. 120" suffix="mg/dL" testId="ha-ppbs" />
                  <NumberField id="ins" label="Fasting Insulin" value={form.fasting_insulin} onChange={update('fasting_insulin')} placeholder="e.g. 8" suffix="µIU/mL" testId="ha-insulin" />
                </div>
              </Section>

              <Section title="Lipid Profile" icon={FlaskConical} accent="bg-gradient-to-br from-emerald-600 to-teal-600" testId="section-lipid">
                <div className="grid grid-cols-2 gap-3">
                  <NumberField id="tc" label="Total Cholesterol" value={form.total_cholesterol} onChange={update('total_cholesterol')} placeholder="mg/dL" suffix="mg/dL" testId="ha-total-chol" />
                  <NumberField id="ldl" label="LDL" value={form.ldl} onChange={update('ldl')} placeholder="mg/dL" suffix="mg/dL" testId="ha-ldl" />
                  <NumberField id="hdl" label="HDL" value={form.hdl} onChange={update('hdl')} placeholder="mg/dL" suffix="mg/dL" testId="ha-hdl" />
                  <NumberField id="tg" label="Triglycerides" value={form.triglycerides} onChange={update('triglycerides')} placeholder="mg/dL" suffix="mg/dL" testId="ha-tg" />
                </div>
              </Section>

              <Section title="Fitness Data" icon={Dumbbell} accent="bg-gradient-to-br from-violet-600 to-fuchsia-600" testId="section-fitness">
                <div className="grid grid-cols-2 gap-3">
                  <NumberField id="rhr" label="Resting HR" value={form.resting_hr} onChange={update('resting_hr')} placeholder="e.g. 65" suffix="bpm" testId="ha-rhr" />
                  <NumberField id="bf" label="Body Fat" value={form.body_fat_pct} onChange={update('body_fat_pct')} placeholder="e.g. 22" suffix="%" testId="ha-bf" />
                </div>
              </Section>

              <Section title="Your Details (for report)" icon={User} accent="bg-gradient-to-br from-slate-700 to-slate-600" testId="section-contact">
                <div className="space-y-3">
                  <div>
                    <label htmlFor="ha-name" className="block text-sm font-medium text-slate-300 mb-1.5">Full Name</label>
                    <input id="ha-name" value={form.full_name} onChange={(e) => update('full_name')(e.target.value)} placeholder="John Doe"
                      className="w-full bg-slate-800/70 border border-slate-700 focus:border-blue-500 focus:outline-none rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-slate-500" data-testid="ha-name" />
                  </div>
                  <div>
                    <label htmlFor="ha-email" className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
                    <input id="ha-email" type="email" value={form.email} onChange={(e) => update('email')(e.target.value)} placeholder="you@example.com"
                      className="w-full bg-slate-800/70 border border-slate-700 focus:border-blue-500 focus:outline-none rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-slate-500" data-testid="ha-email" />
                  </div>
                  <div>
                    <label htmlFor="ha-phone" className="block text-sm font-medium text-slate-300 mb-1.5">Phone (optional)</label>
                    <input id="ha-phone" value={form.phone} onChange={(e) => update('phone')(e.target.value)} placeholder="+91 9XXXXXXXXX"
                      className="w-full bg-slate-800/70 border border-slate-700 focus:border-blue-500 focus:outline-none rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-slate-500" data-testid="ha-phone" />
                  </div>
                </div>
              </Section>
            </div>

            {/* RIGHT: Dashboard (gated by email unlock) */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-6">
              {!analysisUnlocked && (
                <div
                  className="bg-gradient-to-br from-slate-900 via-slate-900/80 to-blue-900/40 border border-blue-500/20 rounded-3xl p-6 sm:p-10 text-center"
                  data-testid="health-email-gate"
                >
                  <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-blue-500/15 border border-blue-400/30 text-blue-300 flex items-center justify-center mb-5">
                    <Stethoscope className="w-7 h-7 sm:w-8 sm:h-8" aria-hidden="true" />
                  </div>
                  <p className="text-blue-300 text-xs font-semibold tracking-[0.18em] uppercase mb-2">Premium Health Report</p>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
                    Enter your details to unlock your analysis
                  </h2>
                  <p className="text-slate-300 text-sm sm:text-base max-w-md mx-auto mb-7 leading-relaxed">
                    Your name and email let us prepare your personalized Tcube Full Body Health Analysis and send follow-up support from our medical team.
                  </p>

                  <div className="max-w-md mx-auto space-y-3 text-left">
                    <div>
                      <label htmlFor="gate-name" className="block text-sm font-medium text-slate-300 mb-1.5">
                        Full Name <span className="text-rose-400">*</span>
                      </label>
                      <input
                        id="gate-name"
                        type="text"
                        value={form.full_name}
                        onChange={(e) => update('full_name')(e.target.value)}
                        placeholder="e.g. Priya Sharma"
                        className="w-full bg-slate-800/70 border border-slate-700 focus:border-blue-500 focus:outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-500"
                        data-testid="gate-name-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="gate-email" className="block text-sm font-medium text-slate-300 mb-1.5">
                        Email Address <span className="text-rose-400">*</span>
                      </label>
                      <input
                        id="gate-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update('email')(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full bg-slate-800/70 border border-slate-700 focus:border-blue-500 focus:outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-500"
                        data-testid="gate-email-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="gate-phone" className="block text-sm font-medium text-slate-300 mb-1.5">
                        Phone (optional)
                      </label>
                      <input
                        id="gate-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update('phone')(e.target.value)}
                        placeholder="+91 9XXXXXXXXX"
                        className="w-full bg-slate-800/70 border border-slate-700 focus:border-blue-500 focus:outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-500"
                        data-testid="gate-phone-input"
                      />
                    </div>

                    {unlockError && (
                      <p className="text-rose-400 text-sm" data-testid="gate-error">{unlockError}</p>
                    )}

                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input type="checkbox" checked={haConsent} onChange={(e) => setHaConsent(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                        data-testid="gate-consent-checkbox" />
                      <span className="text-slate-400 text-xs leading-relaxed">
                        I understand this analysis is for educational purposes and is not a medical diagnosis. I have read the{' '}
                        <a href="/medical-disclaimer" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Medical Disclaimer</a>.
                      </span>
                    </label>

                    <button
                      type="button"
                      onClick={handleUnlock}
                      disabled={unlocking}
                      className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-60 text-white px-6 py-3.5 rounded-full font-bold text-base shadow-lg shadow-blue-500/25 transition-all"
                      data-testid="gate-unlock-btn"
                    >
                      {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {unlocking ? 'Preparing your report…' : 'Unlock My Health Report'}
                    </button>
                    <p className="text-slate-500 text-xs text-center pt-2">
                      Your information stays private and is used only by our medical team for follow-up support.
                    </p>
                  </div>
                </div>
              )}

              {analysisUnlocked && (
              <div className="space-y-6" ref={reportRef}>

              {/* Report header (visible in PDF too) */}
              <div className="bg-gradient-to-br from-slate-900 via-slate-900/80 to-blue-900/40 border border-slate-800 rounded-3xl p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <p className="text-blue-400 text-xs font-semibold tracking-widest uppercase mb-1">Premium Health Report</p>
                    <h2 className="text-xl sm:text-2xl font-bold text-white">Tcube Full Body Health Analysis</h2>
                    <p className="text-slate-400 text-xs sm:text-sm mt-1">
                      {form.full_name ? `Prepared for ${form.full_name}` : 'Live preview'} · {new Date().toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${overallMeta.text} bg-slate-900/70 border ${overallMeta.text.replace('text-', 'border-').replace('-400', '-500/30')}`}>
                    <span className={`w-2 h-2 rounded-full bg-current`} aria-hidden="true"></span>
                    {overallMeta.label}
                  </span>
                </div>

                {/* Overall gauge + radar */}
                <div className="grid sm:grid-cols-2 gap-6 items-center">
                  <div className="flex justify-center">
                    <HealthGauge score={scores.overall} label="Overall Health Score" testId="overall-gauge" />
                  </div>
                  <div className="h-56 sm:h-64" data-testid="health-radar">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData} outerRadius="80%">
                        <PolarGrid stroke="#1e293b" />
                        <PolarAngleAxis dataKey="axis" stroke="#94a3b8" tick={{ fill: '#cbd5e1', fontSize: 11 }} />
                        <PolarRadiusAxis stroke="#1e293b" tick={false} domain={[0, 100]} axisLine={false} />
                        <Radar dataKey="score" stroke="#06b6d4" fill="url(#radar-grad)" fillOpacity={0.5} strokeWidth={2} />
                        <defs>
                          <linearGradient id="radar-grad" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.6} />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0.4} />
                          </linearGradient>
                        </defs>
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Sub-score bars */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid="sub-scores">
                  <ScoreBarCard label="Metabolic Health" score={scores.metabolic} icon={Beaker} testId="score-metabolic" />
                  <ScoreBarCard label="Cardiovascular" score={scores.cardiovascular} icon={Heart} testId="score-cardio" />
                  <ScoreBarCard label="Diabetes Risk" score={scores.diabetes} icon={Droplets} testId="score-diabetes" />
                  <ScoreBarCard label="Obesity Risk" score={scores.obesity} icon={Scale} testId="score-obesity" />
                  <ScoreBarCard label="Fitness Level" score={scores.fitness} icon={Activity} testId="score-fitness" />
                  <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-4 sm:p-5 flex flex-col justify-center" data-testid="completeness-card">
                    <p className="text-slate-300 text-sm font-medium mb-2">Data Completeness</p>
                    <p className="text-2xl font-bold text-cyan-400 tabular-nums">{completeness.percent}%</p>
                    <p className="text-slate-500 text-xs">{completeness.filled}/{completeness.total} markers</p>
                  </div>
                </div>
              </div>

              {/* Submitted data */}
              <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-5 sm:p-6" data-testid="submitted-data-section">
                <h3 className="text-base sm:text-lg font-bold text-white mb-4">Your Submitted Health Data</h3>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {[
                    ['Age', form.age, 'yrs'],
                    ['Gender', form.gender, ''],
                    ['Height', form.height_cm, 'cm'],
                    ['Weight', form.weight_kg, 'kg'],
                    ['Waist', form.waist_cm, 'cm'],
                    ['Hip', form.hip_cm, 'cm'],
                    ['Neck', form.neck_cm, 'cm'],
                    ['Activity', form.activity_level, ''],
                    ['Systolic BP', form.sbp, 'mmHg'],
                    ['Diastolic BP', form.dbp, 'mmHg'],
                    ['HbA1c', form.hba1c, '%'],
                    ['Fasting Glucose', form.fbs, 'mg/dL'],
                    ['Postprandial', form.ppbs, 'mg/dL'],
                    ['Fasting Insulin', form.fasting_insulin, 'µIU/mL'],
                    ['Total Cholesterol', form.total_cholesterol, 'mg/dL'],
                    ['LDL', form.ldl, 'mg/dL'],
                    ['HDL', form.hdl, 'mg/dL'],
                    ['Triglycerides', form.triglycerides, 'mg/dL'],
                    ['Resting HR', form.resting_hr, 'bpm'],
                    ['Body Fat %', form.body_fat_pct, '%']
                  ].map(([label, val, unit]) => {
                    const missing = val === '' || val === null || val === undefined;
                    return (
                      <div key={label} className="flex justify-between border-b border-slate-800 py-1.5">
                        <span className="text-slate-400">{label}</span>
                        <span className={missing ? 'text-slate-600 italic text-xs' : 'text-white font-medium'}>
                          {missing ? 'Not Provided' : `${val} ${unit}`.trim()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Detailed metrics */}
              <div data-testid="detailed-metrics">
                <h3 className="text-base sm:text-lg font-bold text-white mb-3 px-1">Detailed Health Metrics</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <MetricCard icon={Scale} label="BMI" value={computed.bmi} unit="kg/m²" category={computed.bmi_cat} testId="metric-bmi" missingHint="Add height + weight." />
                  <MetricCard icon={Activity} label="Lean Body Mass" value={computed.lbm} unit="kg" testId="metric-lbm" missingHint="Add weight + body fat %." />
                  <MetricCard icon={Ruler} label="Waist-to-Height" value={computed.whtr} category={computed.whtr_cat} testId="metric-whtr" missingHint="Add waist + height." />
                  <MetricCard icon={Ruler} label="Waist-to-Hip" value={computed.whr} category={computed.whr_cat} testId="metric-whr" missingHint="Add waist + hip + gender." />
                  <MetricCard icon={Droplets} label="HbA1c" value={form.hba1c || null} unit="%" category={computed.hba1c_cat} testId="metric-hba1c" missingHint="Add HbA1c." />
                  <MetricCard icon={Droplets} label="Estimated Avg Glucose (eAG)" value={computed.eag} unit="mg/dL" testId="metric-eag" missingHint="Add HbA1c." />
                  <MetricCard icon={Droplets} label="Fasting Glucose" value={form.fbs || null} unit="mg/dL" category={computed.fbs_cat} testId="metric-fbs" missingHint="Add fasting glucose." />
                  <MetricCard icon={Droplets} label="Postprandial Glucose" value={form.ppbs || null} unit="mg/dL" category={computed.ppbs_cat} testId="metric-ppbs" missingHint="Add postprandial." />
                  <MetricCard icon={Beaker} label="HOMA-IR" value={computed.homa_ir} category={computed.homa_cat} testId="metric-homa" missingHint="Add fasting insulin + fasting glucose." />
                  <MetricCard icon={Heart} label="Blood Pressure" value={form.sbp && form.dbp ? `${form.sbp}/${form.dbp}` : null} unit="mmHg" category={computed.bp_cat} testId="metric-bp" missingHint="Add SBP + DBP." />
                  <MetricCard icon={Heart} label="Mean Arterial Pressure (MAP)" value={computed.map} unit="mmHg" category={computed.map_cat} testId="metric-map" missingHint="Add SBP + DBP." />
                  <MetricCard icon={Heart} label="Pulse Pressure" value={computed.pulse_pressure} unit="mmHg" category={computed.pp_cat} testId="metric-pulse" missingHint="Add SBP + DBP." />
                  <MetricCard icon={FlaskConical} label="LDL / HDL Ratio" value={computed.ldl_hdl} category={computed.ldl_hdl_cat} testId="metric-ldl-hdl" missingHint="Add LDL + HDL." />
                  <MetricCard icon={FlaskConical} label="Triglyceride / HDL Ratio" value={computed.tg_hdl} category={computed.tg_hdl_cat} testId="metric-tg-hdl" missingHint="Add triglycerides + HDL." />
                  <MetricCard icon={Dumbbell} label="Resting Heart Rate" value={form.resting_hr || null} unit="bpm" category={computed.resting_hr_cat} testId="metric-rhr" missingHint="Add resting HR." />
                  <MetricCard icon={Activity} label="Body Fat %" value={form.body_fat_pct || null} unit="%" category={computed.body_fat_cat} testId="metric-body-fat" missingHint="Add body fat % + gender." />
                </div>
              </div>

              {/* Unlock more insights */}
              {insufficientHints.length > 0 && (
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-5" data-testid="unlock-hints">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-blue-400" aria-hidden="true" />
                    <h3 className="text-white font-semibold text-sm sm:text-base">Unlock More Insights</h3>
                  </div>
                  <ul className="space-y-2 text-slate-300 text-sm">
                    {insufficientHints.map((h, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <ArrowRight className="w-3.5 h-3.5 text-blue-400 mt-1 flex-shrink-0" aria-hidden="true" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Personalized summary */}
              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-6" data-testid="personalized-summary">
                <div className="flex items-center gap-2 mb-3">
                  <Stethoscope className="w-4 h-4 text-cyan-400" aria-hidden="true" />
                  <h3 className="text-base sm:text-lg font-bold text-white">Personalized Health Summary</h3>
                </div>
                <ul className="space-y-2 text-slate-300 text-sm sm:text-base leading-relaxed">
                  {summary.map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-cyan-400">•</span><span>{s}</span></li>
                  ))}
                </ul>
              </div>

              {/* Recommendations */}
              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-6" data-testid="recommendations">
                <h3 className="text-base sm:text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-emerald-400" aria-hidden="true" />
                  Doctor-Style Recommendations
                </h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  {[
                    { k: 'nutrition', title: 'Nutrition', tone: 'emerald' },
                    { k: 'exercise', title: 'Exercise', tone: 'orange' },
                    { k: 'lifestyle', title: 'Lifestyle Medicine', tone: 'blue' },
                    { k: 'sleep', title: 'Sleep Optimization', tone: 'indigo' },
                    { k: 'hydration', title: 'Hydration', tone: 'cyan' }
                  ].map((sec) => {
                    const items = recommendations[sec.k] || [];
                    if (!items.length) return null;
                    return (
                      <div key={sec.k} className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
                        <p className="text-white font-semibold text-sm sm:text-base mb-2">{sec.title}</p>
                        <ul className="space-y-1.5 text-slate-300 text-xs sm:text-sm">
                          {items.map((it, i) => (
                            <li key={i} className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" /><span>{it}</span></li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5" data-testid="health-disclaimer">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <h3 className="text-amber-200 font-bold text-sm uppercase tracking-wider mb-1">Disclaimer</h3>
                    <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                      This health analysis tool is intended for educational and preventive wellness purposes only and should not replace professional medical diagnosis, treatment, or consultation. Users should consult qualified healthcare professionals for medical advice.
                    </p>
                  </div>
                </div>
              </div>
              </div>
              )}
            </div>
          </div>

          {/* Sticky action bar */}
          {analysisUnlocked && (
          <section className="max-w-7xl mx-auto mt-8">
            <div className="bg-gradient-to-br from-slate-900 to-blue-950/60 border border-blue-500/30 rounded-3xl p-6 sm:p-8 text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Discuss Your Results with a Tcube Expert</h3>
              <p className="text-slate-300 text-sm sm:text-base mb-6 max-w-2xl mx-auto">
                Save your report, download a premium PDF, or speak with our medical team for a personalized plan.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-60 text-white px-5 py-3 rounded-full font-semibold text-sm shadow-lg shadow-blue-500/25"
                  data-testid="ha-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Saving…' : saved ? 'Saved!' : 'Save My Report'}
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={downloadPending}
                  className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-white px-5 py-3 rounded-full font-semibold text-sm border border-slate-700"
                  data-testid="ha-download-pdf-btn"
                >
                  {downloadPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {downloadPending ? 'Generating…' : 'Download PDF'}
                </button>
                <Link to="/book-consultation?from=health-analysis" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-3 rounded-full font-semibold text-sm" data-testid="ha-book-btn">
                  Book Consultation
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <a href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-5 py-3 rounded-full font-semibold text-sm border border-slate-700" data-testid="ha-whatsapp-btn">
                  <MessageCircle className="w-4 h-4" aria-hidden="true" />
                  WhatsApp Doctor
                </a>
              </div>
            </div>
          </section>
          )}

        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default HealthAnalysisPage;
